import React, { Children } from 'react';
import { motion } from 'framer-motion';
const ACCENT = '#3B4A4F';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const stagger = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
type SectionProps = {
  title: string;
  children: React.ReactNode;
};
function Section({
  title,
  children
}: SectionProps) {
  return <motion.section variants={fadeUp} className="mb-12" data-id="element-2739" data-mp="1ma7o28x1qxdg">
      <h2 className="font-serif text-2xl md:text-3xl text-[#1F1F1F] mb-5 leading-[1.2]" data-id="element-2740" data-mp="1r8hhadz7nnwf">
        {title}
      </h2>
      <div className="space-y-4 text-[15px] text-[#3A3A33] leading-relaxed" data-id="element-2741" data-mp="1w956qv7sjwoj">
        {children}
      </div>
    </motion.section>;
}
export function Privacy() {
  const lastUpdated = 'May 2026';
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-2742" data-mp="37lzbm1pi7mz3">
      {/* HERO */}
      <section className="pt-32 md:pt-36 pb-12 md:pb-16 px-6 md:px-10 border-b border-[#E5E5E5]" data-id="element-2743" data-mp="1hm77kcviaz7w">
        <div className="max-w-3xl mx-auto" data-id="element-2744" data-mp="1djkbu0vpsu5f">
          <motion.div initial="hidden" animate="visible" variants={stagger} data-id="element-2745" data-mp="1dkvg01t2o3x2">
            <motion.p variants={fadeUp} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-6" style={{
            color: ACCENT
          }} data-id="element-2746" data-mp="18jpm40noealy">
              Legal
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-serif text-4xl md:text-5xl text-[#1F1F1F] leading-[1.05] mb-5" data-id="element-2747" data-mp="twcy85aebsse">
              Privacy Policy
            </motion.h1>
            <motion.p variants={fadeUp} className="text-sm text-[#5A5A50]" data-id="element-2748" data-mp="kw7rlxd10yfa">
              Last updated: {lastUpdated}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-16 md:py-20 px-6 md:px-10" data-id="element-2749" data-mp="aya79kyaflry">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={stagger} className="max-w-3xl mx-auto" data-id="element-2750" data-mp="19mq21tn13vz">
          <motion.p variants={fadeUp} className="text-[15px] text-[#3A3A33] leading-relaxed mb-12" data-id="element-2751" data-mp="3ofnomva2dwd7">
            Structured Nutrition ("we," "us," or "our") respects your privacy.
            This Privacy Policy explains what information we collect, how we use
            it, and the choices you have. By using our website and services, you
            agree to the practices described below.
          </motion.p>

          <Section title="Information We Collect" data-id="element-2752" data-mp="f9e94gz0hagu">
            <p data-id="element-2753" data-mp="f7ut8rh9p1fl">We collect information you provide directly to us, including:</p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-2754" data-mp="12xxiz0xplvb1">
              <li data-id="element-2755" data-mp="17pglcnfal9ao">
                <strong data-id="element-2756" data-mp="17pyqyanugzv9">Contact details:</strong> Name, email address, and phone
                number when you submit our contact form or sign up for an
                account.
              </li>
              <li data-id="element-2757" data-mp="2nhkfif2d5ctl">
                <strong data-id="element-2758" data-mp="2ng9euo47sl6p">Account information:</strong> Login credentials and
                profile data managed through our authentication provider
                (Firebase).
              </li>
              <li data-id="element-2759" data-mp="2x7vo4ewzsa6t">
                <strong data-id="element-2760" data-mp="2x7cermlequ4e">Payment information:</strong> Processed securely through
                Stripe. We do not store full credit card numbers on our servers.
              </li>
              <li data-id="element-2761" data-mp="24j874vja06z2">
                <strong data-id="element-2762" data-mp="24jq1tnqaid4v">Communications:</strong> Messages you send us through
                forms, email, or social channels.
              </li>
              <li data-id="element-2763" data-mp="2egmc9yzr19c2">
                <strong data-id="element-2764" data-mp="2eg33p8vznxap">Usage data:</strong> Pages visited, time on site, and
                device/browser information collected through cookies and
                analytics tools.
              </li>
            </ul>
          </Section>

          <Section title="How We Use Your Information" data-id="element-2765" data-mp="3pv8aanz8qfzl">
            <p data-id="element-2766" data-mp="1zf60evfulm17">We use the information we collect to:</p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-2767" data-mp="1zgh6j1wrn5vh">
              <li data-id="element-2768" data-mp="24ea97kyv1hr0">Provide and improve our coaching and digital products</li>
              <li data-id="element-2769" data-mp="28et27ichotus">Process payments and deliver purchases</li>
              <li data-id="element-2770" data-mp="28hf87djqf1aw">Respond to inquiries and provide customer support</li>
              <li data-id="element-2771" data-mp="2dfqgvkyaj5rj">
                Send updates, newsletters, or marketing (only if you opt in)
              </li>
              <li data-id="element-2772" data-mp="2ib5tyaoh4xmw">
                Comply with legal obligations and protect against fraud or abuse
              </li>
            </ul>
          </Section>

          <Section title="Third-Party Services" data-id="element-2773" data-mp="2s44k3wzmcw8m">
            <p data-id="element-2774" data-mp="2s5fn3ul8pzyo">
              We share information with trusted third-party providers who help
              us operate our business:
            </p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-2775" data-mp="314szow4nv4nm">
              <li data-id="element-2776" data-mp="362l2il6ugb6l">
                <strong data-id="element-2777" data-mp="3621jt29ddiri">Stripe</strong> — payment processing
              </li>
              <li data-id="element-2778" data-mp="3g6hy3r77art7">
                <strong data-id="element-2779" data-mp="3g71g17wupg78">Firebase (Google)</strong> — authentication and database
              </li>
              <li data-id="element-2780" data-mp="3b7f36zkxq1yy">
                <strong data-id="element-2781" data-mp="3b6wz5gs16jgh">Formspree</strong> — contact form submissions
              </li>
              <li data-id="element-2782" data-mp="369n0dair4vfz">
                <strong data-id="element-2783" data-mp="3693tcoun3rgq">Google Analytics / Google Tag Manager</strong> — site
                analytics
              </li>
              <li data-id="element-2784" data-mp="329356chqo1wh">
                <strong data-id="element-2785" data-mp="2rvco8bnexnl">Meta Pixel</strong> — advertising and analytics
              </li>
            </ul>
            <p data-id="element-2786" data-mp="3tyg07i2hvnfv">
              These providers have their own privacy policies governing how they
              handle your data.
            </p>
          </Section>

          <Section title="Cookies and Tracking" data-id="element-2787" data-mp="3p5mxt0ui0r6y">
            <p data-id="element-2788" data-mp="3l6e3nuvjqrav">
              We use cookies and similar technologies to remember your
              preferences, keep you logged in, analyze site traffic, and measure
              advertising effectiveness. You can disable cookies in your browser
              settings, though some site features may stop working as a result.
            </p>
          </Section>

          <Section title="Your Rights" data-id="element-2789" data-mp="2b1uip1r74jyu">
            <p data-id="element-2790" data-mp="2fyuqpi7lvwsl">Depending on where you live, you may have the right to:</p>
            <ul className="list-disc list-outside pl-6 space-y-2" data-id="element-2791" data-mp="219yjfnddint1">
              <li data-id="element-2792" data-mp="219f32b3dg7h4">Access the personal information we hold about you</li>
              <li data-id="element-2793" data-mp="267gubjaybd3o">Request that we correct or delete your information</li>
              <li data-id="element-2794" data-mp="1s8h5obnfuy7i">Opt out of marketing communications at any time</li>
              <li data-id="element-2795" data-mp="1sbd3lzzadmvy">
                Withdraw consent for processing where consent is the legal basis
              </li>
            </ul>
            <p data-id="element-2796" data-mp="1ibm9hzen19we">
              To exercise any of these rights, contact us using the details
              below.
            </p>
          </Section>

          <Section title="Data Security" data-id="element-2797" data-mp="1nc070nim51g6">
            <p data-id="element-2798" data-mp="3coc64owe4x7h">
              We use reasonable administrative, technical, and physical
              safeguards to protect your information. However, no method of
              transmission over the internet is 100% secure, and we cannot
              guarantee absolute security.
            </p>
          </Section>

          <Section title="Children's Privacy" data-id="element-2799" data-mp="112jnd1u96q1i">
            <p data-id="element-2800" data-mp="10znqzhy209f6">
              Our services are not directed to individuals under the age of 18.
              We do not knowingly collect personal information from children. If
              you believe a child has provided us information, please contact us
              so we can delete it.
            </p>
          </Section>

          <Section title="Changes to This Policy" data-id="element-2801" data-mp="1kq0wt1nasss5">
            <p data-id="element-2802" data-mp="1kp7tkanjsler">
              We may update this Privacy Policy from time to time. When we do,
              we'll revise the "Last updated" date at the top of this page.
              Significant changes will be communicated via the site or email.
            </p>
          </Section>

          <Section title="Contact Us" data-id="element-2803" data-mp="4brh0vx4gccx">
            <p data-id="element-2804" data-mp="3vhivuz5g82kf">
              Questions about this Privacy Policy or your data? Reach out
              through our{' '}
              <a href="/contact" className="underline hover:no-underline" style={{
              color: ACCENT
            }} data-id="element-2805" data-mp="39owmdle4typi">
                contact page
              </a>
              .
            </p>
          </Section>

          <motion.p variants={fadeUp} className="text-xs text-[#5A5A50] italic mt-16 pt-8 border-t border-[#E5E5E5]" data-id="element-2806" data-mp="3odsu1hca19pl">
            This document is provided as a general template and does not
            constitute legal advice. We recommend reviewing it with an attorney
            before relying on it.
          </motion.p>
        </motion.div>
      </section>
    </div>;
}